/**
 * Shared Checkout Types
 *
 * Central definitions used across all checkout pages
 * (payment, customer, shipping, review, success, failure, pending).
 */

// ─── sessionStorage key registry ────────────────────────────────────────────────

/**
 * Single source of truth for every sessionStorage key used in the checkout flow.
 * Import this object instead of hard-coding key strings, so renames are
 * applied uniformly across all pages.
 */
export const CHECKOUT_KEYS = {
  /** Cart items – one key per product id: `order-<productId>` (prefix) */
  ORDER_PREFIX: 'order-',
  /**
   * Unified shipping selection.
   * Shape: ICheckoutShippingStorage
   * Written by: cart order-summary + shipping page.
   * Read  by : shipping page (restore), review page (fetch full method).
   */
  SHIPPING: 'checkout_shipping',
  /**
   * Payment method selection.
   * Shape: ICheckoutPaymentStorage
   * Written by: payment page.
   * Read  by : review page (fetch full method).
   */
  PAYMENT: 'checkout_payment',
  /** Customer information form data. */
  CUSTOMER: 'checkout_customer_info',
  /**
   * Applied coupon object (ICoupon).
   * Written by: cart order-summary on checkout navigation.
   * Read  by : review page (restore applied coupon).
   */
  COUPON: 'checkout_coupon',
  /** Set to '1' after Purchase pixel fires to prevent duplicate conversions on refresh. */
  PIXEL_PURCHASE_FIRED: 'checkout_pixel_purchase_fired',
} as const;

// ─── Payment ──────────────────────────────────────────────────────────────────

/** Supported payment method codes */
export type IPaymentType = 'MERCADOPAGO' | 'BANK_TRANSFER' | 'CREDIT_CARD' | 'DEBIT_CARD' | 'PRISMA' | 'PAYWAY' | 'FISERV' | 'GETNET' | 'PAGO_360';

// ─── Customer ─────────────────────────────────────────────────────────────────

/** Customer form data saved to sessionStorage as `checkout_customer_info` */
export interface ICustomerInfo {
  first_name?: string;
  last_name?: string;
  name?: string; // legacy/display name
  email: string;
  /** ISO date string (YYYY-MM-DD) */
  birthdate: string;
  /** National ID / DNI */
  id_number: string;
  id_type?: string;
  phone: string;
  enterprise?: string;
  tax_id?: string;
  customer_type?: string;
  status?: string;
  /** Session user id – stored for reference only */
  userId?: string | number | null;
}

// ─── Shipping ─────────────────────────────────────────────────────────────────

/** Address form data for home-delivery shipping */
export interface IAddressData {
  zipcode: string;
  street_name: string;
  street_number: string;
  apartment?: string;
  location: string;
  province?: string;
  address_type?: 'SHIPPING' | 'BILLING';
}

/** Shipping method as mapped from IShippingProvider and stored in sessionStorage */
export interface IShippingMethod {
  id: string;
  /** Provider-specific code used to identify the shipping option (e.g. 'CRAYON_LOCAL_PICKUP', '94584') */
  code?: string;
  name: string;
  description: string;
  cost: number;
  cost_type?: 'FIXED' | 'PERCENTAGE';
  estimatedDays?: string;
  provider?: string;
  /** 'DELIVERY' | 'TAKEAWAY' | 'STANDARD' | 'EXPRESS' */
  type?: 'DELIVERY' | 'TAKEAWAY' | 'STANDARD' | 'EXPRESS';
  providerId?: string;
  locationsEndpoint?: string;
  freeShippingThreshold?: number;
}

/** Shape written to `cart_shipping_preload` by the cart page — DEPRECATED, use ICheckoutShippingStorage */
export interface ICartShippingPreload {
  method?: IShippingMethod;
  location?: import('@/services').IShippingLocation;
  locationId?: string;
}

/**
 * Shape written to `CHECKOUT_KEYS.SHIPPING` by both the cart order-summary
 * and the dedicated shipping page.
 *
 * Design rationale:
 * - Only `methodId` is stored for the shipping *method*; the full object is
 *   fetched from the server on the review page. This avoids stale price/name
 *   data and prevents accidental leaking of server-side config into the browser.
 * - The pickup `location` (TAKEAWAY) is stored in full because it is user-
 *   display data that is not always re-fetchable by a single ID.
 * - The delivery `address` (DELIVERY) is stored in full because it is
 *   user-entered data that lives only in this flow.
 */
export interface ICheckoutShippingStorage {
  /** ID of the chosen shipping provider/method (used to re-fetch on review). */
  methodId: number | string;
  /** Full location object – populated only for TAKEAWAY methods. */
  location?: import('@/services').IShippingLocation;
  /** Delivery address – populated only for DELIVERY methods. */
  address?: IAddressData | Record<string, unknown>;
  /** Backend address record id (set after address creation API call). */
  addressId?: string | number;
}

/**
 * Shape written to `CHECKOUT_KEYS.PAYMENT` by the payment page.
 *
 * Only the method id is persisted; the full pay-method object (including
 * gateway credentials) is re-fetched from the server on the review page.
 */
export interface ICheckoutPaymentStorage {
  /** Code/id of the selected pay method (e.g. 'MERCADOPAGO', 'BANK_TRANSFER'). */
  methodId: string;
}

// ─── MercadoPago Callback ─────────────────────────────────────────────────────

/** Query-string params injected by MercadoPago on return URLs (success / failure / pending) */
export interface IMercadoPagoCallbackParams {
  collection_id?: string;
  collection_status?: string;
  payment_id?: string;
  status?: string;
  external_reference?: string;
  payment_type?: string;
  merchant_order_id?: string;
  preference_id?: string;
  site_id?: string;
  processing_mode?: string;
  merchant_account_id?: string;
}

/** Next.js page props for MercadoPago callback pages (success / failure) */
export interface IMercadoPagoPageProps {
  searchParams: Promise<IMercadoPagoCallbackParams>;
}

// ─── Pending Page ─────────────────────────────────────────────────────────────

/** Query-string params for the pending payment page */
export interface ICheckoutPendingParams {
  orderId?: string;
  /** Cart UUID – same field MP populates on success/failure callbacks */
  external_reference?: string;
  payment_id?: string;
  status?: string;
}

/** Next.js page props for the pending page */
export interface ICheckoutPendingPageProps {
  searchParams: Promise<ICheckoutPendingParams>;
}
